'use client';

import styles from './ClientChatList.module.css';
import type { ClientChatListProps } from './ClientChatList.types';
import { Input, Flex, ButtonGroup, Button } from '@/_components';
import { ChatItem } from './components/ChatItem';
import { CompanyContext } from '@/_contexts/admin/CompanyProvider';
import { Conversation, Message } from '@/_types/models/conversations';
import { ClientConversationsContext } from '@/_contexts/client/ClientConversationsProvider';
import {
  useContext,
  useMemo,
  useState,
  type FC,
  type ChangeEvent,
  useEffect,
  useCallback,
} from 'react';
import { getAllConversationNames } from '@/_utils/conversations';
import { UserContext } from '@/_contexts/UserProvider';
import { ChatItemLoading } from './components/ChatItemLoading';
import { useDebounce } from '@/_hooks/useDebounce';
import { findConversations } from '@/_api/v2/conversations';

const FILTER_DEBOUNCE = 640 as const;

export const ClientChatList: FC<ClientChatListProps> = ({
  isAdvisor,
  shouldShowAllConversations,
  showUnreadOnly,
}) => {
  const [isLimiting, setLimiting] = useState<boolean>(true);
  const [filter, setFilter] = useState<string>('');
  const [isSearchLoading, setSearchLoading] = useState<boolean>(false);
  const [foundConversations, setFoundConversations] = useState<
    Array<Conversation>
  >([]);

  const searchDebounce: string = useDebounce<string>(filter, FILTER_DEBOUNCE);

  const { user, token } = useContext(UserContext);
  const { isConversationsLoading, selectedCompanyConversations } =
    useContext(CompanyContext);
  const { conversations, conversationIdNumber, isLoading } = useContext(
    ClientConversationsContext,
  );

  const standaloneConversations: Array<Conversation> = useMemo(() => {
    if (shouldShowAllConversations) return conversations;

    return isAdvisor
      ? isConversationsLoading
        ? []
        : selectedCompanyConversations
      : conversations;
  }, [
    conversations,
    isAdvisor,
    isConversationsLoading,
    shouldShowAllConversations,
    selectedCompanyConversations,
  ]);

  const filteredConversations: Array<Conversation> = useMemo(() => {
    return standaloneConversations?.filter((c) => {
      if (!showUnreadOnly) return true;

      const unreadCount: number =
        c.unseen_messages?.filter(
          (um: Message) =>
            !um?.is_log && um?.unseen_user_ids?.includes?.(user?.id ?? -1),
        )?.length ?? 0;

      return unreadCount > 0;
    });
  }, [standaloneConversations, showUnreadOnly, user]);

  const isLimited: boolean = useMemo(() => {
    return Boolean(
      !shouldShowAllConversations &&
        !isLoading &&
        isLimiting &&
        isAdvisor &&
        filteredConversations?.length > 1,
    );
  }, [
    isAdvisor,
    isLimiting,
    isLoading,
    shouldShowAllConversations,
    filteredConversations?.length,
  ]);

  const allConversations: Array<Conversation> = useMemo(() => {
    if (!filteredConversations?.length || isSearchLoading) return [];
    if (!!searchDebounce) return foundConversations;

    const hasActiveConversation: boolean = filteredConversations.some(
      (c: Conversation): boolean => c.id === conversationIdNumber,
    );

    if (typeof conversationIdNumber === 'number' && !hasActiveConversation) {
      console.error('ConversationItemMissingError');

      return [];
    }

    return isLimited
      ? filteredConversations?.filter((c) => c.id === conversationIdNumber)
      : filteredConversations;
  }, [
    searchDebounce,
    isSearchLoading,
    foundConversations,
    isLimited,
    filteredConversations,
    conversationIdNumber,
  ]);

  const _onSearch = useCallback(
    async (newSearchTerm: string) => {
      if (!newSearchTerm) {
        setSearchLoading(false);
        setFoundConversations([]);

        return;
      }

      setSearchLoading(true);

      try {
        const conversations = await findConversations({
          search: newSearchTerm,
          token,
        });

        setFoundConversations(conversations);
      } catch (error) {
        console.error(error);
      } finally {
        setSearchLoading(false);
      }
    },
    [token],
  );

  useEffect(() => {
    _onSearch(searchDebounce);
  }, [searchDebounce, _onSearch]);

  if (!isAdvisor) return null;

  const onRemoveEntityFilter = () => setLimiting(false);
  const onClearFilter = () => setFilter('');

  const onFilterChange = async (event: ChangeEvent<HTMLInputElement>) =>
    setFilter(event.target.value);

  return (
    <Flex isColumn={true} gap={6} padding={6} className={styles.chatList}>
      <Flex isColumn={true} gap={2}>
        {shouldShowAllConversations && (
          <Flex className={styles.chatList__search}>
            <Input
              label="Filter"
              placeholder="Filter"
              autoComplete="off"
              isLabelHidden={true}
              value={filter}
              iconRight={filter ? 'x' : undefined}
              onRightClick={filter ? onClearFilter : undefined}
              onChange={onFilterChange}
              type="text"
            />
          </Flex>
        )}
        {(isLoading || isSearchLoading) && <ChatItemLoading />}
        {allConversations?.map((conversation, key) => {
          const isActive: boolean = conversationIdNumber === conversation.id;
          const conversationNames: Array<string> = getAllConversationNames(
            conversation,
            isAdvisor,
          );

          const isGroup: boolean = conversation?.user_ids?.length > 1;

          return (
            <ChatItem
              key={key}
              isAdvisor={isAdvisor}
              conversation={conversation}
              name={conversationNames?.join(', ')}
              type={!isAdvisor ? 'firm' : isGroup ? 'group' : 'user'}
              isActive={isActive}
            />
          );
        })}
      </Flex>
      {isLimited && (
        <ButtonGroup align="center">
          <Button
            intent="secondary"
            iconLeft="chats"
            value="Show All"
            onClick={onRemoveEntityFilter}
            size="small"
          />
        </ButtonGroup>
      )}
    </Flex>
  );
};
